import { Icon } from "@iconify/react";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { apiRequest } from "../../redux/api";
import { languageSwitcherOptions, LanguageCode } from "../../localization/supportedLanguages";
import { setLanguage } from "../../redux/features/language/languageSlice";
import { RootState } from "../../redux/store";
import { buildAppLink, redirectForAttachment, redirectForChat } from "../../utils/landingRedirect";
import "./YouWoConsumerLanding.css";

type StripePrice = {
  amount: number | null;
  currency: string;
  interval?: string;
};

type StripeTier = {
  monthly?: StripePrice | null;
  yearly?: StripePrice | null;
};

type StripePricesResponse = {
  currency?: string;
  tiers?: {
    plus?: StripeTier;
    pro?: StripeTier;
    max?: StripeTier;
  };
};

type ConsumerPlanId = "free" | "plus" | "pro" | "max";
type PaidConsumerPlanId = Exclude<ConsumerPlanId, "free" | "max">;
type ConsumerLandingVariant = "default" | "source";

type ConsumerCopy = {
  nav: {
    product: string;
    pricing: string;
    signIn: string;
    start: string;
    languageLabel: string;
  };
  hero: {
    titleStart: string;
    titleAccent: string;
    titleEnd: string;
    lead: string;
    placeholder: string;
    send: string;
    attach: string;
  };
  proof: string[];
  availability: {
    label: string;
    ios: string;
    desktop: string;
  };
  featureTabs: {
    kicker: string;
    tabs: Array<{
      icon: string;
      label: string;
      title: string;
      body: string;
      bullets: string[];
      prompt: string;
    }>;
  };
  demo: {
    title: string;
    sources: string[];
    output: string;
    items: string[];
  };
  flow: {
    kicker: string;
    title: string;
    cards: Array<{ icon: string; title: string; body: string }>;
  };
  pricing: {
    kicker: string;
    title: string;
    body: string;
    monthly: string;
    yearly: string;
    perMonth: string;
    perYear: string;
    billedMonthly: string;
    billedYearlyPrefix: string;
    billedYearlySuffix: string;
    yearlySaveSuffix: string;
    startFree: string;
    upgrade: string;
    subscribe: string;
    popular: string;
    plans: Record<ConsumerPlanId, {
      name: string;
      subtitle: string;
      price?: string;
      features: string[];
    }>;
  };
};

type SourceWorkflowCopy = {
  navCta: string;
  heroLead: string;
  promptPlaceholder: string;
  askCta: string;
  demoLabel: string;
  sourceTitle: string;
  sourceSubtitle: string;
  recordingTitle: string;
  recordingStatus: string;
  transcriptTimestamp: string;
  transcript: string;
  translation: string;
  uploadTitle: string;
  uploadItems: Array<{ icon: string; label: string; detail: string }>;
  connectedTitle: string;
  connectedItems: Array<{ icon: string; label: string; detail: string }>;
  askLabel: string;
  question: string;
  answerLabel: string;
  answer: string;
  workflowLabel: string;
  workflowTitle: string;
  workflowSubtitle: string;
  workActions: string[];
  citations: Array<{ icon: string; label: string; detail: string }>;
  wedge: {
    kicker: string;
    title: string;
    body: string;
    cards: Array<{ icon: string; title: string; body: string }>;
  };
};

const promptExamples = {
  en: [
    "Summarize this meeting and list follow-ups",
    "Turn this PDF into an executive brief",
    "Find deadlines and owners across these notes",
    "Draft a reply from this context",
  ],
  zh: [
    "总结这场会议并列出 follow-up",
    "把这份 PDF 变成 executive brief",
    "找出这些记录里的截止日期和负责人",
    "根据上下文帮我起草回复",
  ],
  ja: [
    "この会議を要約してフォローアップを出して",
    "この PDF をエグゼクティブブリーフにして",
    "このメモから締切と担当者を見つけて",
    "この文脈から返信文を作って",
  ],
} satisfies Record<LanguageCode, string[]>;

const consumerCopy = {
  en: {
    nav: {
      product: "What it does",
      pricing: "Pricing",
      signIn: "Sign in",
      start: "Start free",
      languageLabel: "Switch language",
    },
    hero: {
      titleStart: "Ask YouWo",
      titleAccent: "anything",
      titleEnd: "from your context",
      lead: "Record, upload, or connect the materials you already work from. YouWo organizes the sources, understands what matters, and helps you get cited answers, summaries, drafts, and follow-ups.",
      placeholder: "Ask about a meeting, PDF, note, email thread, or follow-up...",
      send: "Start",
      attach: "Attach context",
    },
    proof: ["500 daily free credits", "Record, upload, or connect", "Source-backed answers", "Memory on paid plans"],
    availability: {
      label: "Also available",
      ios: "Download iOS",
      desktop: "Download desktop",
    },
    featureTabs: {
      kicker: "Start with context",
      tabs: [
        {
          icon: "lucide:mic",
          label: "Meetings",
          title: "Ask across every recording",
          body: "Record or upload a meeting, then ask what was decided, what changed, and who owns the next step without re-listening.",
          bullets: ["Timestamp citations", "Decisions and blockers", "Follow-up draft"],
          prompt: "What did we decide in this meeting?",
        },
        {
          icon: "lucide:mail",
          label: "Email",
          title: "Bring email into the same context",
          body: "Use connected or pasted email context with meetings and files so answers and drafts reflect the full story.",
          bullets: ["Thread summary", "Reply draft", "Source-aware context"],
          prompt: "Use this email thread and meeting to draft my reply",
        },
        {
          icon: "lucide:search",
          label: "Research",
          title: "Read across sources without losing the trail",
          body: "Compare PDFs, notes, links, and recordings, then get a source-backed answer or brief you can verify.",
          bullets: ["Source-backed brief", "Contradictions and risks", "Cited answers"],
          prompt: "Compare these documents and give me a sourced recommendation",
        },
        {
          icon: "lucide:brain",
          label: "Memory",
          title: "Let YouWo learn how you work",
          body: "On paid plans, YouWo can preserve useful preferences and repeatable patterns so future answers and drafts fit your style.",
          bullets: ["Working preferences", "Reusable patterns", "Better follow-ups"],
          prompt: "Remember this as my preferred client update format",
        },
      ],
    },
    demo: {
      title: "From scattered context to a grounded answer",
      sources: ["Meeting recording", "Project PDF", "Client notes"],
      output: "YouWo answers with sources",
      items: ["Cited answer", "Summary", "Draft", "Next steps"],
    },
    flow: {
      kicker: "How it feels",
      title: "First it understands your context. Then it can do work your way.",
      cards: [
        { icon: "lucide:upload-cloud", title: "Capture or connect", body: "Record a meeting, upload PDFs, paste links, add images, or bring in supported service context." },
        { icon: "lucide:brain", title: "Understand the source", body: "YouWo extracts structure, key points, owners, deadlines, evidence, and exact citations." },
        { icon: "lucide:sparkles", title: "Ask or create", body: "Get answers, summaries, briefs, follow-ups, explanations, or writing help from the same grounded context." },
      ],
    },
    pricing: {
      kicker: "Pricing",
      title: "Start free. Upgrade when your workload gets bigger.",
      body: "Monthly and yearly plans use the same credit model as the app. Yearly shows the effective monthly price and the billed yearly amount.",
      monthly: "Monthly",
      yearly: "Yearly",
      perMonth: "CAD / month",
      perYear: "/ year",
      billedMonthly: "Billed monthly",
      billedYearlyPrefix: "Billed",
      billedYearlySuffix: "yearly",
      yearlySaveSuffix: "off",
      startFree: "Start free",
      upgrade: "Upgrade",
      subscribe: "Subscribe to",
      popular: "Popular",
      plans: {
        free: {
          name: "Free",
          subtitle: "Try YouWo with daily credits",
          price: "0",
          features: ["500 daily credits", "Basic AI help", "Ask about meetings, docs, and notes", "Great for trying one workflow"],
        },
        plus: {
          name: "Plus",
          subtitle: "21,000 monthly AI credits for everyday work",
          features: ["21,000 monthly AI credits", "More room for recordings, imports, and chat", "Summaries, flashcards, quizzes, and folder chat", "Recording, translation, and imports are billed from credits", "Larger uploads and source-backed tools", "Good for everyday context Q&A"],
        },
        pro: {
          name: "Pro",
          subtitle: "55,000 monthly AI credits with advanced models and higher limits",
          features: ["All Plus benefits", "55,000 monthly AI credits", "Advanced models for research and cited work", "Memory for preferences and working patterns", "Fast transcription and translation use credits", "Higher document, image, and audio import limits"],
        },
        max: {
          name: "Enterprise Max",
          subtitle: "For companies and power users with high-volume workflows",
          features: ["1,200,000+ monthly credits", "Highest public model access", "More room for large projects", "Scoped workflow and connector setup", "Built for intensive AI workflows"],
        },
      },
    },
  },
  zh: {
    nav: {
      product: "功能",
      pricing: "价格",
      signIn: "登录",
      start: "免费开始",
      languageLabel: "切换语言",
    },
    hero: {
      titleStart: "让 YouWo",
      titleAccent: "回答任何问题",
      titleEnd: "基于你的上下文",
      lead: "录音、上传，或连接你已经在使用的资料。YouWo 会整理来源、理解重点，并帮你得到带引用的回答、总结、草稿和 follow-up",
      placeholder: "围绕会议、PDF、笔记、邮件线程或 follow-up 提问...",
      send: "开始",
      attach: "添加上下文",
    },
    proof: ["每天 500 免费 credits", "录音、上传或连接", "带来源的回答", "付费计划支持记忆"],
    availability: {
      label: "也可以下载",
      ios: "下载 iOS",
      desktop: "下载桌面端",
    },
    featureTabs: {
      kicker: "从上下文开始",
      tabs: [
        {
          icon: "lucide:mic",
          label: "会议",
          title: "围绕每段录音提问",
          body: "录制或上传会议后，直接问做了什么决定、发生了什么变化、下一步由谁负责，不用重新听一遍",
          bullets: ["时间戳引用", "决策和阻塞点", "Follow-up 草稿"],
          prompt: "这场会议最后决定了什么？",
        },
        {
          icon: "lucide:mail",
          label: "邮件",
          title: "把邮件放进同一个上下文",
          body: "连接或粘贴邮件内容，并和会议、文件一起使用，让回答和草稿理解完整背景",
          bullets: ["邮件线程总结", "回复草稿", "带来源上下文"],
          prompt: "结合这封邮件和会议记录，帮我起草回复",
        },
        {
          icon: "lucide:search",
          label: "研究",
          title: "跨资料阅读但不丢来源",
          body: "比较 PDF、记录、链接和录音，然后生成可以核查来源的回答或 brief",
          bullets: ["带来源的 brief", "矛盾点和风险", "带引用的回答"],
          prompt: "比较这些资料并给我一个带来源的建议",
        },
        {
          icon: "lucide:brain",
          label: "记忆",
          title: "让 YouWo 理解你的工作方式",
          body: "在付费计划中，YouWo 可以保存有用偏好和重复模式，让以后的回答和草稿更符合你的风格",
          bullets: ["工作偏好", "可复用模式", "更贴合的 follow-up"],
          prompt: "记住这是我常用的客户更新格式",
        },
      ],
    },
    demo: {
      title: "从分散上下文到带来源回答",
      sources: ["会议录音", "项目 PDF", "客户记录"],
      output: "YouWo 给出带来源回答",
      items: ["带引用回答", "总结", "草稿", "下一步"],
    },
    flow: {
      kicker: "使用感受",
      title: "先理解你的上下文，再按你的方式推进工作",
      cards: [
        { icon: "lucide:upload-cloud", title: "捕捉或连接", body: "录会议、上传 PDF、粘贴链接、添加图片，或接入支持的服务上下文" },
        { icon: "lucide:brain", title: "理解来源", body: "YouWo 提取结构、重点、负责人、截止日期、证据和精确引用" },
        { icon: "lucide:sparkles", title: "提问或创作", body: "基于同一份可信上下文生成回答、总结、brief、follow-up、解释或写作帮助" },
      ],
    },
    pricing: {
      kicker: "价格",
      title: "免费开始，任务变多后再升级",
      body: "月付和年付沿用 app 内同一套 credit 模型。年付显示折算后的月费和实际年付金额",
      monthly: "月付",
      yearly: "年付",
      perMonth: "CAD / 月",
      perYear: "/ 年",
      billedMonthly: "按月收取",
      billedYearlyPrefix: "每年收取",
      billedYearlySuffix: "",
      yearlySaveSuffix: "优惠",
      startFree: "免费开始",
      upgrade: "升级",
      subscribe: "订阅",
      popular: "推荐",
      plans: {
        free: {
          name: "Free",
          subtitle: "用每日 credits 试用 YouWo",
          price: "0",
          features: ["每天 500 credits", "基础 AI 帮助", "围绕会议、文档和记录提问", "适合尝试一个工作流"],
        },
        plus: {
          name: "Plus",
          subtitle: "21,000 monthly AI credits，适合日常工作",
          features: ["每月 21,000 AI credits", "更多录音、导入和聊天额度", "总结、闪卡、测验和文件夹聊天", "录音、翻译和导入会从 credits 扣除", "更大的上传额度和带来源工具", "适合日常上下文问答"],
        },
        pro: {
          name: "Pro",
          subtitle: "55,000 monthly AI credits，包含高级模型和更高额度",
          features: ["包含所有 Plus benefits", "每月 55,000 AI credits", "高级模型用于研究和带引用工作", "记住偏好和工作模式", "高速转录和翻译使用 credits", "更高的文档、图片和音频导入额度"],
        },
        max: {
          name: "Enterprise Max",
          subtitle: "适合企业和高频工作流",
          features: ["每月 1,200,000+ credits", "最高公开模型访问", "大型项目空间更充足", "按需配置工作流和连接器", "适合密集 AI 工作流"],
        },
      },
    },
  },
  ja: {
    nav: {
      product: "できること",
      pricing: "料金",
      signIn: "ログイン",
      start: "無料で始める",
      languageLabel: "言語を切り替え",
    },
    hero: {
      titleStart: "YouWo に",
      titleAccent: "何でも聞く",
      titleEnd: "あなたの文脈から",
      lead: "録音、アップロード、または普段使う資料を接続。YouWo がソースを整理し、重要点を理解して、引用付き回答、要約、下書き、フォローアップを作ります",
      placeholder: "会議、PDF、メモ、メールスレッド、フォローアップについて質問...",
      send: "始める",
      attach: "文脈を追加",
    },
    proof: ["毎日 500 無料 credits", "録音・アップロード・接続", "根拠付き回答", "有料プランでメモリ対応"],
    availability: {
      label: "アプリでも利用可能",
      ios: "iOS をダウンロード",
      desktop: "デスクトップ版をダウンロード",
    },
    featureTabs: {
      kicker: "文脈から始める",
      tabs: [
        {
          icon: "lucide:mic",
          label: "会議",
          title: "すべての録音に質問する",
          body: "会議を録音またはアップロードし、決定事項、変更点、次の担当者を聞けます。聞き直す必要はありません",
          bullets: ["タイムスタンプ引用", "決定事項とブロッカー", "フォローアップ下書き"],
          prompt: "この会議で何が決まりましたか？",
        },
        {
          icon: "lucide:mail",
          label: "メール",
          title: "メールを同じ文脈に入れる",
          body: "接続または貼り付けたメールを会議やファイルと合わせ、全体の背景を理解した回答と下書きを作ります",
          bullets: ["スレッド要約", "返信下書き", "ソースを踏まえた文脈"],
          prompt: "このメールと会議を使って返信文を作って",
        },
        {
          icon: "lucide:search",
          label: "リサーチ",
          title: "ソースを失わずに横断して読む",
          body: "PDF、メモ、リンク、録音を比較し、確認できる根拠付きの回答やブリーフを作成します",
          bullets: ["根拠付きブリーフ", "矛盾点とリスク", "引用付き回答"],
          prompt: "これらの資料を比較して根拠付きの提案を出して",
        },
        {
          icon: "lucide:brain",
          label: "メモリ",
          title: "YouWo があなたの働き方を理解する",
          body: "有料プランでは、YouWo が有用な好みや繰り返しパターンを保存し、次回以降の回答や下書きをあなたのスタイルに近づけます",
          bullets: ["作業の好み", "再利用できる型", "より良いフォローアップ"],
          prompt: "これを顧客更新の標準フォーマットとして覚えて",
        },
      ],
    },
    demo: {
      title: "散らばった文脈から根拠付き回答へ",
      sources: ["会議録音", "プロジェクト PDF", "顧客メモ"],
      output: "YouWo がソース付きで回答",
      items: ["引用付き回答", "要約", "下書き", "次のステップ"],
    },
    flow: {
      kicker: "使い心地",
      title: "まず文脈を理解し、その後あなたのやり方で仕事を進める",
      cards: [
        { icon: "lucide:upload-cloud", title: "取り込む・接続する", body: "会議録音、PDF、リンク、画像、対応サービスの文脈を追加" },
        { icon: "lucide:brain", title: "ソースを理解する", body: "構造、要点、担当者、締切、根拠、正確な引用を抽出" },
        { icon: "lucide:sparkles", title: "質問・作成する", body: "同じ根拠付き文脈から回答、要約、ブリーフ、フォローアップ、説明、文章作成を依頼" },
      ],
    },
    pricing: {
      kicker: "料金",
      title: "無料で始めて、必要になったらアップグレード",
      body: "月額と年額はアプリと同じ credit モデルです。年額は月あたり換算と年間請求額を表示します",
      monthly: "月額",
      yearly: "年額",
      perMonth: "CAD / 月",
      perYear: "/ 年",
      billedMonthly: "月額請求",
      billedYearlyPrefix: "年額",
      billedYearlySuffix: "請求",
      yearlySaveSuffix: "OFF",
      startFree: "無料で始める",
      upgrade: "アップグレード",
      subscribe: "Subscribe to",
      popular: "おすすめ",
      plans: {
        free: {
          name: "Free",
          subtitle: "毎日の credits で YouWo を試す",
          price: "0",
          features: ["毎日 500 credits", "基本 AI サポート", "会議、文書、メモについて質問", "ひとつのワークフローを試すのに最適"],
        },
        plus: {
          name: "Plus",
          subtitle: "日常業務向けの 21,000 monthly AI credits",
          features: ["毎月 21,000 AI credits", "録音、取り込み、チャットにより多く使える", "要約、単語カード、クイズ、フォルダーチャット", "録音、翻訳、取り込みは credits を使用", "より大きなアップロードと根拠付きツール", "日常的な文脈 Q&A に最適"],
        },
        pro: {
          name: "Pro",
          subtitle: "高度なモデルと上限を含む 55,000 monthly AI credits",
          features: ["Plus のすべての benefits", "毎月 55,000 AI credits", "研究と引用付き作業向けの高度なモデル", "好みと作業パターンのメモリ", "高速文字起こしと翻訳は credits を使用", "文書、画像、音声の取り込み上限アップ"],
        },
        max: {
          name: "Enterprise Max",
          subtitle: "企業と高ボリュームワークフロー向け",
          features: ["毎月 1,200,000+ credits", "公開プラン最高のモデルアクセス", "大規模プロジェクトに余裕", "ワークフローとコネクターをスコープして設定", "集中的な AI ワークフロー向け"],
        },
      },
    },
  },
} satisfies Record<LanguageCode, ConsumerCopy>;

const sourceWorkflowCopy = {
  en: {
    navCta: "Open",
    heroLead: "Give YouWo the actual sources first: record in real time, translate when needed, upload files, or connect the services where work already lives. Then ask, generate, and run workflow steps from sources you can verify.",
    promptPlaceholder: "Ask, generate, or run a workflow...",
    askCta: "Run",
    demoLabel: "How the wedge works",
    sourceTitle: "Sources come in first",
    sourceSubtitle: "Live, uploaded, and connected context land in the same workspace.",
    recordingTitle: "Live recording",
    recordingStatus: "Recording and translating",
    transcriptTimestamp: "02:18",
    transcript: "Move the launch review to Friday. Maya owns legal copy, Alex owns the pricing update, and the Q3 brief has the final numbers.",
    translation: "将发布评审移到周五。Maya 负责法律文案，Alex 负责价格更新，Q3 brief 有最终数字。",
    uploadTitle: "Uploaded files",
    uploadItems: [
      { icon: "lucide:file-text", label: "Q3 brief.pdf", detail: "PDF, page citations" },
      { icon: "lucide:image", label: "Whiteboard photo", detail: "Image notes" },
    ],
    connectedTitle: "Connected services",
    connectedItems: [
      { icon: "lucide:mail", label: "Email thread", detail: "Client replies" },
      { icon: "lucide:folder-sync", label: "Drive folder", detail: "Project docs" },
    ],
    askLabel: "Ask once",
    question: "What changed, who owns it, and what should I send?",
    answerLabel: "Source-backed answer",
    answer: "The launch review moved to Friday. Maya owns legal copy; Alex owns pricing. Send a short confirmation with the Q3 brief attached and mention the updated date.",
    workflowLabel: "Answer into workflow",
    workflowTitle: "Draft the reply, create the follow-up, keep the sources attached.",
    workflowSubtitle: "YouWo can answer, generate the artifact, and prepare the next workflow step from the same context.",
    workActions: ["Draft reply", "Create follow-up", "Prepare workflow"],
    citations: [
      { icon: "lucide:audio-lines", label: "Recording", detail: "02:18" },
      { icon: "lucide:file-text", label: "Q3 brief", detail: "p. 4" },
      { icon: "lucide:mail", label: "Email", detail: "client thread" },
    ],
    wedge: {
      kicker: "The version to test",
      title: "Sell the source-backed work loop, not a pile of features.",
      body: "Recording, translation, uploads, and integrations matter because they make the answer trustworthy. The first sell should be simple: bring in the real context, ask what to do next, and open the sources behind the answer.",
      cards: [
        { icon: "lucide:mic", title: "Capture the live source", body: "Record meetings in real time, keep the transcript, and translate when the source language changes." },
        { icon: "lucide:paperclip", title: "Add the rest of the context", body: "Upload PDFs, images, notes, links, and audio so the app has the materials people actually work from." },
        { icon: "lucide:plug", title: "Connect services for workflows", body: "For higher-value workflows, connect third-party tools so YouWo can prepare source-grounded drafts and next steps." },
      ],
    },
  },
  zh: {
    navCta: "打开",
    heroLead: "先把真实来源交给 YouWo：实时录音、需要时翻译、上传文件，或连接工作已经发生的服务。然后基于可核查来源提问、生成内容，并推进工作流步骤。",
    promptPlaceholder: "提问、生成内容，或运行工作流...",
    askCta: "运行",
    demoLabel: "核心切入点",
    sourceTitle: "先进入真实来源",
    sourceSubtitle: "实时、上传和连接的上下文进入同一个工作区。",
    recordingTitle: "实时录音",
    recordingStatus: "正在录音并翻译",
    transcriptTimestamp: "02:18",
    transcript: "把 launch review 移到周五。Maya 负责法律文案，Alex 负责价格更新，Q3 brief 里有最终数字。",
    translation: "Move the launch review to Friday. Maya owns legal copy, Alex owns the pricing update, and the Q3 brief has the final numbers.",
    uploadTitle: "上传文件",
    uploadItems: [
      { icon: "lucide:file-text", label: "Q3 brief.pdf", detail: "PDF，页码引用" },
      { icon: "lucide:image", label: "白板照片", detail: "图片记录" },
    ],
    connectedTitle: "连接服务",
    connectedItems: [
      { icon: "lucide:mail", label: "邮件线程", detail: "客户回复" },
      { icon: "lucide:folder-sync", label: "Drive 文件夹", detail: "项目文档" },
    ],
    askLabel: "问一次",
    question: "发生了什么变化，谁负责，我应该发什么？",
    answerLabel: "带来源回答",
    answer: "Launch review 改到周五。Maya 负责法律文案，Alex 负责价格。建议发一封简短确认，并附上 Q3 brief，说明新的日期。",
    workflowLabel: "从回答到工作流",
    workflowTitle: "起草回复、创建 follow-up，并保留来源。",
    workflowSubtitle: "YouWo 可以基于同一份上下文回答、生成内容，并准备下一步工作。",
    workActions: ["起草回复", "创建 follow-up", "准备工作流"],
    citations: [
      { icon: "lucide:audio-lines", label: "录音", detail: "02:18" },
      { icon: "lucide:file-text", label: "Q3 brief", detail: "第 4 页" },
      { icon: "lucide:mail", label: "邮件", detail: "客户线程" },
    ],
    wedge: {
      kicker: "这版要测试的方向",
      title: "卖带来源的工作闭环，不卖一堆功能。",
      body: "录音、翻译、上传和集成之所以重要，是因为它们让回答可信。第一层卖点应该很简单：带入真实上下文，问下一步怎么做，并能打开回答背后的来源。",
      cards: [
        { icon: "lucide:mic", title: "捕捉实时来源", body: "实时录会议，保留转录，并在语言切换时翻译。" },
        { icon: "lucide:paperclip", title: "加入剩余上下文", body: "上传 PDF、图片、笔记、链接和音频，让 app 拥有人们真正工作的资料。" },
        { icon: "lucide:plug", title: "连接服务形成工作流", body: "在更高价值的工作流里，连接第三方工具，让 YouWo 准备带来源的草稿和下一步。" },
      ],
    },
  },
  ja: {
    navCta: "開く",
    heroLead: "まず実際のソースを YouWo に渡します。リアルタイム録音、必要な翻訳、ファイルアップロード、仕事があるサービス接続。その後、確認できる根拠から質問、生成、ワークフロー実行まで進めます。",
    promptPlaceholder: "質問、生成、ワークフロー実行...",
    askCta: "実行",
    demoLabel: "コアの切り口",
    sourceTitle: "ソースが先に入る",
    sourceSubtitle: "ライブ、アップロード、接続された文脈が同じワークスペースに集まります。",
    recordingTitle: "ライブ録音",
    recordingStatus: "録音と翻訳中",
    transcriptTimestamp: "02:18",
    transcript: "Launch review を金曜日に移動。Maya が法務コピー、Alex が価格更新を担当し、Q3 brief に最終数字があります。",
    translation: "Move the launch review to Friday. Maya owns legal copy, Alex owns the pricing update, and the Q3 brief has the final numbers.",
    uploadTitle: "アップロード",
    uploadItems: [
      { icon: "lucide:file-text", label: "Q3 brief.pdf", detail: "PDF、ページ引用" },
      { icon: "lucide:image", label: "ホワイトボード写真", detail: "画像メモ" },
    ],
    connectedTitle: "接続サービス",
    connectedItems: [
      { icon: "lucide:mail", label: "メールスレッド", detail: "顧客返信" },
      { icon: "lucide:folder-sync", label: "Drive フォルダ", detail: "プロジェクト資料" },
    ],
    askLabel: "一度聞く",
    question: "何が変わり、誰が担当で、何を送るべき？",
    answerLabel: "ソース付き回答",
    answer: "Launch review は金曜日に変更。Maya が法務コピー、Alex が価格を担当。Q3 brief を添付し、更新日を伝える短い確認を送るのがよいです。",
    workflowLabel: "回答からワークフローへ",
    workflowTitle: "返信を下書きし、フォローアップを作り、ソースを残す。",
    workflowSubtitle: "YouWo は同じ文脈から回答、生成、次の作業準備まで進めます。",
    workActions: ["返信を下書き", "フォローアップ作成", "ワークフロー準備"],
    citations: [
      { icon: "lucide:audio-lines", label: "録音", detail: "02:18" },
      { icon: "lucide:file-text", label: "Q3 brief", detail: "p. 4" },
      { icon: "lucide:mail", label: "メール", detail: "顧客スレッド" },
    ],
    wedge: {
      kicker: "この版で試す方向",
      title: "機能の山ではなく、ソース付きの仕事ループを売る。",
      body: "録音、翻訳、アップロード、連携が重要なのは、回答を信頼できるものにするからです。最初の訴求はシンプルに、実際の文脈を入れ、次に何をすべきか聞き、回答の根拠を開けることです。",
      cards: [
        { icon: "lucide:mic", title: "ライブソースを取る", body: "会議をリアルタイム録音し、文字起こしを残し、必要に応じて翻訳します。" },
        { icon: "lucide:paperclip", title: "残りの文脈を追加", body: "PDF、画像、メモ、リンク、音声をアップロードし、実際に使う資料を集めます。" },
        { icon: "lucide:plug", title: "サービス接続でワークフロー化", body: "高価値な作業では外部ツールを接続し、根拠付きの下書きと次のアクションを準備します。" },
      ],
    },
  },
} satisfies Record<LanguageCode, SourceWorkflowCopy>;

const fallbackMonthlyPrices: Record<string, Record<Exclude<ConsumerPlanId, "free">, number>> = {
  USD: { plus: 1999, pro: 4999, max: 9999 },
  CAD: { plus: 2499, pro: 5999, max: 11999 },
  CNY: { plus: 3500, pro: 6800, max: 20000 },
};

const fallbackYearlyPrices: Record<string, Record<Exclude<ConsumerPlanId, "free">, number>> = {
  USD: { plus: 19999, pro: 49999, max: 99999 },
  CAD: { plus: 24999, pro: 59999, max: 119999 },
  CNY: { plus: 30000, pro: 68800, max: 204800 },
};

const currencySymbol = (currency: string) => {
  if (currency.toUpperCase() === "CNY") return "¥";
  if (currency.toUpperCase() === "CAD") return "CA$";
  return "$";
};

const formatCents = (amount: number) => {
  const rounded = Math.round(amount);
  const dollars = Math.floor(rounded / 100);
  const cents = rounded % 100;
  return cents > 0 ? `${dollars}.${String(cents).padStart(2, "0")}` : `${dollars}`;
};

const getTierAmount = (
  tier: Exclude<ConsumerPlanId, "free">,
  interval: "monthly" | "yearly",
  prices: StripePricesResponse | null,
) => {
  const apiAmount = prices?.tiers?.[tier]?.[interval]?.amount;
  if (typeof apiAmount === "number") {
    return apiAmount;
  }

  const currency = prices?.currency?.toUpperCase() || "CAD";
  const table = interval === "monthly" ? fallbackMonthlyPrices : fallbackYearlyPrices;
  return (table[currency] || table.USD)[tier];
};

const getYearlyMonthlyEquivalent = (yearlyAmount: number) => Math.floor(yearlyAmount / 12);

const getYearlySavings = (
  monthlyAmount: number,
  yearlyAmount: number,
) => Math.max(0, Math.round((1 - yearlyAmount / (monthlyAmount * 12)) * 100));

const logoMaskStyle: React.CSSProperties = {
  WebkitMask: 'url("/logo/logo.png") center / contain no-repeat',
  mask: 'url("/logo/logo.png") center / contain no-repeat',
};

const SourceWorkflowDemo: React.FC<{ copy: SourceWorkflowCopy }> = ({ copy }) => {
  const recordingCitation = copy.citations[0];
  const uploadedFile = copy.uploadItems[0];
  const uploadedImage = copy.uploadItems[1];
  const emailSource = copy.connectedItems[0];
  const driveSource = copy.connectedItems[1];
  const activityItems = [
    {
      className: "yw-source-popup-recording",
      icon: "lucide:audio-lines",
      label: copy.recordingTitle,
      detail: `${copy.transcriptTimestamp} · ${copy.recordingStatus}`,
    },
    {
      className: "yw-source-popup-upload",
      icon: uploadedFile?.icon ?? "lucide:file-text",
      label: uploadedFile?.label ?? copy.uploadTitle,
      detail: uploadedFile?.detail ?? copy.uploadTitle,
    },
    {
      className: "yw-source-popup-email",
      icon: emailSource?.icon ?? "lucide:mail",
      label: emailSource?.label ?? copy.connectedTitle,
      detail: emailSource?.detail ?? copy.connectedTitle,
    },
    {
      className: "yw-source-popup-drive",
      icon: driveSource?.icon ?? "lucide:folder-sync",
      label: driveSource?.label ?? copy.connectedTitle,
      detail: driveSource?.detail ?? copy.connectedTitle,
    },
  ];

  return (
    <section className="yw-source-demo" id="product" aria-label={copy.demoLabel}>
      <div className="yw-source-demo-header">
        <p>{copy.demoLabel}</p>
        <h2>{copy.sourceTitle}</h2>
        <span>{copy.sourceSubtitle}</span>
      </div>
      <div className="yw-app-preview-shell">
        <div className="yw-app-preview-topbar">
          <Icon icon="lucide:chevron-left" />
          <strong>{copy.recordingTitle}</strong>
          <Icon icon="lucide:more-horizontal" />
        </div>
        <div className="yw-app-preview-tabs" aria-hidden="true">
          <span>Summary</span>
          <span className="yw-app-preview-tab-active">Transcript</span>
          <span>AI Chat</span>
          <span>Note</span>
          <span>Workflow</span>
        </div>
        <div className="yw-app-preview-grid">
          <section className="yw-app-transcript-pane" aria-label={copy.recordingTitle}>
            <article className="yw-app-transcript-line">
              <span>{copy.transcriptTimestamp}</span>
              <p>{copy.transcript}</p>
              <em>{copy.translation}</em>
            </article>
            <article className="yw-app-transcript-line">
              <span>02:36</span>
              <p>{copy.workflowSubtitle}</p>
            </article>
            <div className="yw-app-source-strip" aria-label={copy.uploadTitle}>
              <div>
                <Icon icon={uploadedFile?.icon ?? "lucide:file-text"} />
                <span>{uploadedFile?.label ?? copy.uploadTitle}</span>
              </div>
              <div>
                <Icon icon={uploadedImage?.icon ?? "lucide:image"} />
                <span>{uploadedImage?.label ?? copy.uploadTitle}</span>
              </div>
              <div>
                <Icon icon={emailSource?.icon ?? "lucide:mail"} />
                <span>{emailSource?.label ?? copy.connectedTitle}</span>
              </div>
              <div>
                <Icon icon={driveSource?.icon ?? "lucide:folder-sync"} />
                <span>{driveSource?.label ?? copy.connectedTitle}</span>
              </div>
            </div>
          </section>

          <aside className="yw-app-work-pane">
            <div className="yw-app-user-bubble">
              <Icon icon="lucide:message-circle" />
              <span>{copy.question}</span>
            </div>
            <section className="yw-app-answer-panel">
              <p>{copy.workflowLabel}</p>
              <h3>{copy.workflowTitle}</h3>
              <span>{copy.answer}</span>
              <div className="yw-inline-citation-row">
                {copy.citations.map((citation, index) => (
                  <button
                    aria-label={`${citation.label} ${citation.detail}`}
                    className={`yw-inline-citation yw-inline-citation-${index}`}
                    key={`${citation.label}-${citation.detail}`}
                    type="button"
                  >
                    <Icon icon={citation.icon} />
                    <strong>{citation.label}</strong>
                    <span>{citation.detail}</span>
                  </button>
                ))}
              </div>
              <div className="yw-work-action-row">
                {copy.workActions.map((action) => (
                  <button key={action} type="button">
                    {action}
                  </button>
                ))}
              </div>
            </section>
            <div className="yw-source-popup-stage" aria-label={copy.connectedTitle}>
              {activityItems.map((item) => (
                <div className={`yw-source-popup ${item.className}`} key={item.label}>
                  <Icon icon={item.icon} />
                  <div>
                    <strong>{item.label}</strong>
                    <span>{item.detail}</span>
                    <i aria-hidden="true" />
                  </div>
                </div>
              ))}
            </div>
            {recordingCitation ? (
              <div className="yw-citation-preview" aria-label={`${recordingCitation.label} ${recordingCitation.detail}`}>
                <div className="yw-citation-preview-header">
                  <span>
                    <Icon icon={recordingCitation.icon} />
                  </span>
                  <strong>{recordingCitation.label} {recordingCitation.detail}</strong>
                  <small>{copy.answerLabel}</small>
                </div>
                <p>{copy.transcript}</p>
              </div>
            ) : null}
          </aside>
        </div>

        <div className="yw-app-recording-controls" aria-hidden="true">
          <div className="yw-app-recording-tools">
            <Icon icon="lucide:image" />
            <Icon icon="lucide:camera" />
            <Icon icon="lucide:highlighter" />
          </div>
          <div className="yw-recording-meter">
            <span className="yw-recording-dot" />
            {[0, 1, 2, 3, 4, 5].map((bar) => (
              <span className={`yw-volume-bar yw-volume-bar-${bar}`} key={bar} />
            ))}
            <strong>01:05</strong>
          </div>
          <div className="yw-app-recording-actions">
            <Icon icon="lucide:pause" />
            <span><Icon icon="lucide:square" /></span>
          </div>
        </div>
      </div>
    </section>
  );
};

const SourceWedgeSection: React.FC<{ copy: SourceWorkflowCopy }> = ({ copy }) => (
  <section className="yw-flow-section yw-source-wedge-section">
    <p>{copy.wedge.kicker}</p>
    <h2>{copy.wedge.title}</h2>
    <span>{copy.wedge.body}</span>
    <div className="yw-flow-grid">
      {copy.wedge.cards.map((card) => (
        <article key={card.title}>
          <Icon icon={card.icon} />
          <h3>{card.title}</h3>
          <p>{card.body}</p>
        </article>
      ))}
    </div>
  </section>
);

type YouWoConsumerLandingProps = {
  variant?: ConsumerLandingVariant;
};

const YouWoConsumerLanding: React.FC<YouWoConsumerLandingProps> = ({ variant = "default" }) => {
  const { i18n } = useTranslation();
  const dispatch = useDispatch();
  const currentLanguage = useSelector((state: RootState) => state.language.current);
  const copy: ConsumerCopy = consumerCopy[currentLanguage] ?? consumerCopy.en;
  const sourceCopy = sourceWorkflowCopy[currentLanguage] ?? sourceWorkflowCopy.en;
  const isSourceVariant = variant === "source";
  const [prompt, setPrompt] = useState("");
  const [activeFeatureIndex, setActiveFeatureIndex] = useState(0);
  const [languageOpen, setLanguageOpen] = useState(false);
  const [billingInterval, setBillingInterval] = useState<"monthly" | "yearly">("monthly");
  const [prices, setPrices] = useState<StripePricesResponse | null>(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const response = await apiRequest({
          url: "subscriptions/stripe-prices-public",
          method: "GET",
          useAuthHeader: false,
        });

        if (!cancelled) {
          setPrices(response);
        }
      } catch {
        if (!cancelled) {
          setPrices({ currency: "CAD" });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const currency = prices?.currency?.toUpperCase() || "CAD";
  const visiblePlans = useMemo<PaidConsumerPlanId[]>(() => ["plus", "pro"], []);
  const activeFeature = copy.featureTabs.tabs[activeFeatureIndex] ?? copy.featureTabs.tabs[0];

  const handleLanguageChange = (language: LanguageCode) => {
    dispatch(setLanguage(language));
    i18n.changeLanguage(language);
    setLanguageOpen(false);
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    redirectForChat(prompt.trim() || promptExamples[currentLanguage][0]);
  };

  return (
    <main className="yw-consumer-page" data-lang={currentLanguage} data-variant={variant}>
      <nav className="yw-consumer-nav" aria-label="YouWo consumer landing navigation">
        <a className="yw-consumer-brand" href="/internal/youwo-consumer-landing">
          <span aria-hidden="true" className="yw-consumer-logo-mark" style={logoMaskStyle} />
          <strong>YouWo</strong>
        </a>
        <div className="yw-consumer-nav-links">
          <a href="#product">{copy.nav.product}</a>
          <a href="#pricing">{copy.nav.pricing}</a>
        </div>
        <div className="yw-consumer-nav-actions">
          <div className="yw-consumer-language-menu">
            <button
              aria-expanded={languageOpen}
              aria-label={copy.nav.languageLabel}
              className="yw-consumer-language-trigger"
              onClick={() => setLanguageOpen((isOpen) => !isOpen)}
              type="button"
            >
              {languageSwitcherOptions.find((option) => option.code === currentLanguage)?.shortLabel ?? "EN"}
              <Icon icon="lucide:chevron-down" />
            </button>
            {languageOpen ? (
              <div className="yw-consumer-language-popover">
                {languageSwitcherOptions.map((option) => (
                  <button
                    className={option.code === currentLanguage ? "yw-consumer-language-option yw-consumer-language-option-active" : "yw-consumer-language-option"}
                    key={option.code}
                    onClick={() => handleLanguageChange(option.code)}
                    type="button"
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
          <a className="yw-consumer-start" href={buildAppLink()}>{isSourceVariant ? sourceCopy.navCta : copy.nav.start}</a>
        </div>
      </nav>

      <section className="yw-consumer-hero">
        <div className="yw-consumer-hero-copy">
          <h1>
            {copy.hero.titleStart}{" "}
            <span>{copy.hero.titleAccent}</span>{" "}
            {copy.hero.titleEnd}
          </h1>
          <p>{isSourceVariant ? sourceCopy.heroLead : copy.hero.lead}</p>
        </div>

        <form className="yw-capture-box" onSubmit={handleSubmit}>
          <textarea
            aria-label={isSourceVariant ? sourceCopy.promptPlaceholder : copy.hero.placeholder}
            onChange={(event) => setPrompt(event.target.value)}
            placeholder={isSourceVariant ? sourceCopy.promptPlaceholder : copy.hero.placeholder}
            rows={3}
            value={prompt}
          />
          <div className="yw-capture-footer">
            <button className="yw-capture-attach" onClick={redirectForAttachment} type="button">
              <Icon icon="lucide:paperclip" />
              {copy.hero.attach}
            </button>
            <button className="yw-capture-submit" type="submit">
              {isSourceVariant ? sourceCopy.askCta : copy.hero.send}
              <Icon icon="lucide:arrow-up" />
            </button>
          </div>
        </form>

        {!isSourceVariant ? (
          <div className="yw-example-row">
            {promptExamples[currentLanguage].map((example) => (
              <button key={example} onClick={() => setPrompt(example)} type="button">{example}</button>
            ))}
          </div>
        ) : null}

        {!isSourceVariant ? (
          <div className="yw-platform-row" aria-label={copy.availability.label}>
            <span>{copy.availability.label}</span>
            <a href="https://apps.apple.com/app/youwo-ai-agent/id6760689478" target="_blank" rel="noopener noreferrer">
              <Icon icon="logos:apple" />
              {copy.availability.ios}
            </a>
            <a href="https://downloads.youwo.ai/mac/YouWo-latest.dmg" target="_blank" rel="noopener noreferrer">
              <Icon icon="lucide:monitor-down" />
              {copy.availability.desktop}
            </a>
          </div>
        ) : null}

        {isSourceVariant ? (
          <>
            <SourceWorkflowDemo copy={sourceCopy} />
            <div className="yw-platform-row" aria-label={copy.availability.label}>
              <span>{copy.availability.label}</span>
              <a href="https://apps.apple.com/app/youwo-ai-agent/id6760689478" target="_blank" rel="noopener noreferrer">
                <Icon icon="logos:apple" />
                {copy.availability.ios}
              </a>
              <a href="https://downloads.youwo.ai/mac/YouWo-latest.dmg" target="_blank" rel="noopener noreferrer">
                <Icon icon="lucide:monitor-down" />
                {copy.availability.desktop}
              </a>
            </div>
          </>
        ) : (
          <section className="yw-feature-tabs" aria-label={copy.featureTabs.kicker}>
            <p>{copy.featureTabs.kicker}</p>
            <div className="yw-feature-tab-list" role="tablist">
              {copy.featureTabs.tabs.map((feature, index) => (
                <button
                  aria-selected={activeFeatureIndex === index}
                  className={activeFeatureIndex === index ? "yw-feature-tab yw-feature-tab-active" : "yw-feature-tab"}
                  key={feature.label}
                  onClick={() => setActiveFeatureIndex(index)}
                  role="tab"
                  type="button"
                >
                  <Icon icon={feature.icon} />
                  {feature.label}
                </button>
              ))}
            </div>
            <div className="yw-feature-panel" key={activeFeature.label}>
              <div>
                <h2>{activeFeature.title}</h2>
                <p>{activeFeature.body}</p>
              </div>
              <ul>
                {activeFeature.bullets.map((bullet) => (
                  <li key={bullet}><Icon icon="lucide:check" />{bullet}</li>
                ))}
              </ul>
              <button onClick={() => setPrompt(activeFeature.prompt)} type="button">
                {activeFeature.prompt}
                <Icon icon="lucide:arrow-right" />
              </button>
            </div>
          </section>
        )}
      </section>

      {isSourceVariant ? (
        <SourceWedgeSection copy={sourceCopy} />
      ) : (
        <>
          <section className="yw-consumer-demo" id="product">
            <div className="yw-demo-window">
              <div className="yw-window-dots"><span /><span /><span /></div>
              <div className="yw-demo-grid">
                <aside className="yw-demo-sources">
                  {copy.demo.sources.map((source, index) => (
                    <div className={`yw-demo-source yw-demo-source-${index}`} key={source}>
                      <Icon icon={index === 0 ? "lucide:audio-lines" : index === 1 ? "lucide:file-text" : "lucide:presentation"} />
                      <span>{source}</span>
                    </div>
                  ))}
                </aside>
                <section className="yw-demo-output">
                  <p>{copy.demo.title}</p>
                  <h2>{copy.demo.output}</h2>
                  <div>
                    {copy.demo.items.map((item) => (
                      <span key={item}><Icon icon="lucide:check" />{item}</span>
                    ))}
                  </div>
                </section>
              </div>
            </div>
          </section>

          <section className="yw-flow-section">
            <p>{copy.flow.kicker}</p>
            <h2>{copy.flow.title}</h2>
            <div className="yw-flow-grid">
              {copy.flow.cards.map((card) => (
                <article key={card.title}>
                  <Icon icon={card.icon} />
                  <h3>{card.title}</h3>
                  <p>{card.body}</p>
                </article>
              ))}
            </div>
          </section>
        </>
      )}

      <section className="yw-pricing-section" id="pricing">
        <div className="yw-section-heading">
          <p>{copy.pricing.kicker}</p>
          <h2>{copy.pricing.title}</h2>
          <span>{copy.pricing.body}</span>
        </div>
        <div className="yw-billing-toggle" role="group" aria-label="Billing interval">
          <button
            className={billingInterval === "monthly" ? "yw-billing-active" : ""}
            onClick={() => setBillingInterval("monthly")}
            type="button"
          >
            {copy.pricing.monthly}
          </button>
          <button
            className={billingInterval === "yearly" ? "yw-billing-active" : ""}
            onClick={() => setBillingInterval("yearly")}
            type="button"
          >
            {copy.pricing.yearly}
          </button>
        </div>

        <div className="yw-price-grid">
          {visiblePlans.map((planId) => {
            const planCopy = copy.pricing.plans[planId];
            const monthlyAmount = getTierAmount(planId, "monthly", prices);
            const yearlyAmount = getTierAmount(planId, "yearly", prices);
            const displayAmount = billingInterval === "monthly" ? monthlyAmount : getYearlyMonthlyEquivalent(yearlyAmount);
            const priceLabel = `${currencySymbol(currency)}${formatCents(displayAmount)}`;
            const billingNote = billingInterval === "monthly"
              ? copy.pricing.billedMonthly
              : `${copy.pricing.billedYearlyPrefix} ${currencySymbol(currency)}${formatCents(yearlyAmount)} ${copy.pricing.billedYearlySuffix}`.trim();
            const yearlySavings = getYearlySavings(monthlyAmount, yearlyAmount);

            return (
              <article className={planId === "plus" ? "yw-price-card yw-price-card-popular" : "yw-price-card"} key={planId}>
                {planId === "plus" ? <span className="yw-popular-pill">{copy.pricing.popular}</span> : null}
                <h3>{planCopy.name}</h3>
                <p>{planCopy.subtitle}</p>
                <div className="yw-price-value">
                  <strong>{priceLabel}</strong>
                  <span>{copy.pricing.perMonth}</span>
                </div>
                <p className="yw-price-billing">
                  {billingNote}
                  {billingInterval === "yearly" ? <span>{yearlySavings}% {copy.pricing.yearlySaveSuffix}</span> : null}
                </p>
                <ul>
                  {planCopy.features.map((feature) => (
                    <li key={feature}><Icon icon="lucide:check" />{feature}</li>
                  ))}
                </ul>
                <a href={buildAppLink("/pricing")}>{copy.pricing.subscribe} {planCopy.name}</a>
              </article>
            );
          })}
        </div>
      </section>
    </main>
  );
};

export default YouWoConsumerLanding;
